import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { Button, FormControl, Select, MenuItem, FormHelperText, InputLabel, TextField } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import React from 'react';

RHFMultipleSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  variant: PropTypes.string,
  width: PropTypes.number,
  noOptionsText: PropTypes.string,
  search: PropTypes.bool,
  outputFormat: PropTypes.oneOf(['array', 'string']),
  allowExclusionsFromAll: PropTypes.bool,
  selectAll: PropTypes.bool,
};

export default function RHFMultipleSelectField({
  name,
  options,
  variant,
  label,
  width = 150,
  search = false,
  noOptionsText = 'No options available',
  outputFormat = 'array',
  // Opt-in only — every other caller of this component is completely unaffected. When true and
  // 'ALL' is selected, the user can also toggle individual options off from the SAME dropdown to
  // mean "ALL except these" instead of needing a second exclusion field. The stored field value in
  // this state is ['ALL', ...excludedValues] (or 'ALL,x,y' for string format) — values listed after
  // 'ALL' are EXCLUDED, not included (the inverse of normal, no-ALL selections).
  allowExclusionsFromAll = false,
  // Opt-in: a "Select all / Clear" row at the top of the menu (acting on the options the search
  // shows), and a "N selected" summary once more than two are picked. Stores the picked values
  // themselves, never 'ALL'.
  selectAll = false,
  placeholder,
  ...other
}) {
  const { control } = useFormContext();
  const noOptionsAvailable = options.length === 0;
  const uniqueId = React.useMemo(() => `multiple-select-${name}-${Date.now()}`, [name]);
  const [searchText, setSearchText] = React.useState('');
  const searchInputRef = React.useRef(null);

  // Every real (non-'ALL') option value — needed to expand "ALL except x,y" into the full
  // MUI-facing selection so the built-in multi-select toggle logic still works per-checkbox.
  const allRealValues = React.useMemo(() => options.map(o => o.value).filter(v => v !== 'ALL'), [options]);

  // Filter options based on search text
  const filteredOptions = search
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchText.toLowerCase()))
    : options;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Handle both array and string formats for display
        let selectedValues;
        if (outputFormat === 'string') {
          if (typeof field.value === 'string') {
            selectedValues = field.value === 'ALL' ? ['ALL'] : field.value.split(',').filter(v => v.trim() !== '');
          } else {
            selectedValues = Array.isArray(field.value) ? field.value : [];
          }
        } else {
          // For array format, always ensure we have an array
          selectedValues = Array.isArray(field.value) ? field.value : [];
        }
        const isAllSelected = selectedValues.includes('ALL');
        // In exclusion mode, everything stored after 'ALL' is an exclusion, not an inclusion.
        const excludedValues = (allowExclusionsFromAll && isAllSelected) ? selectedValues.filter(v => v !== 'ALL') : [];

        // What MUI's <Select> itself treats as "currently checked" — drives its own internal
        // per-item toggle logic. When 'ALL' is active (exclusion mode or not) this must be the
        // full option list (minus exclusions, if any) rather than just ['ALL']: otherwise MUI has
        // no notion that every real option reads as checked, so clicking one to uncheck it can't
        // compute "everything except that one" — it just sees 'ALL' toggled back on and the whole
        // selection collapses to ['ALL'] again, making individual items impossible to deselect.
        const muiValue = isAllSelected
          ? (allowExclusionsFromAll
              ? ['ALL', ...allRealValues.filter(v => !excludedValues.includes(v))]
              : ['ALL', ...allRealValues])
          : selectedValues;

        const commitValue = (next) => {
          if (outputFormat === 'string') {
            field.onChange(next.length === 1 && next[0] === 'ALL' ? 'ALL' : next.join(','));
          } else {
            field.onChange(next);
          }
        };

        const showSelectAll = selectAll && !allowExclusionsFromAll && !noOptionsAvailable;
        const pickedValues = isAllSelected ? allRealValues : selectedValues;
        const shownValues = filteredOptions.map(o => o.value).filter(v => v !== 'ALL');
        const allShownPicked = shownValues.length > 0 && shownValues.every(v => pickedValues.includes(v));
        const pickShown = () => commitValue([...new Set([...pickedValues, ...shownValues])]);
        const clearShown = () => commitValue(searchText ? pickedValues.filter(v => !shownValues.includes(v)) : []);

        const handleChange = (event) => {
          const rawNext = event.target.value;

          if (allowExclusionsFromAll && rawNext.includes('ALL') && !isAllSelected) {
            // Freshly entering "ALL" mode from a non-ALL (or blank) selection. MUI's own
            // multi-select state only just toggled the "ALL" checkbox itself on — it has no
            // notion that every real option should now read as checked too, so rawNext here
            // is just ['ALL'] alone. Treating that literally as "everyone except rawNext" would
            // exclude every real merchant on the very first click. Start clean: all included.
            commitValue(['ALL']);
          } else if (allowExclusionsFromAll && rawNext.includes('ALL')) {
            // Still in "ALL" mode — whichever real options are NOT present in MUI's reported
            // selection are the ones the user just excluded (or re-included).
            const newExcluded = allRealValues.filter(v => !rawNext.includes(v));
            commitValue(['ALL', ...newExcluded]);
          } else if (!allowExclusionsFromAll && rawNext.includes('ALL') && !isAllSelected) {
            // Non-exclusion mode, freshly choosing ALL: collapse to plain ALL, same as this
            // component always did.
            commitValue(['ALL']);
          } else if (!allowExclusionsFromAll && rawNext.includes('ALL') && isAllSelected) {
            // Non-exclusion mode, already in ALL, and 'ALL' is still present in what MUI reports:
            // the user unchecked one specific item (muiValue included every real value, so the
            // click removed just that one). Drop out of ALL mode into a plain partial selection of
            // whatever's left checked, so the deselected item actually stays deselected instead of
            // the whole thing snapping back to ['ALL'].
            const stillChecked = rawNext.filter(v => v !== 'ALL');
            commitValue(stillChecked.length === allRealValues.length ? ['ALL'] : stillChecked);
          } else if (!allowExclusionsFromAll && isAllSelected) {
            // Non-exclusion mode: 'ALL' itself was just unchecked (rawNext lost it, not any one
            // real item) — clear the whole selection, like a master "select all" checkbox turning off.
            commitValue([]);
          } else {
            commitValue(rawNext);
          }

          // Restore focus to search input after selection
          if (search && searchInputRef.current) {
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }
        };

        return (
          <FormControl variant={variant || "outlined"} error={!!error} sx={{ minWidth: width, maxWidth: width }}>
            <InputLabel id={`demo-multiple-${uniqueId}`}>{label}</InputLabel>
            <Select
              {...field}
              multiple
              labelId={`demo-multiple-${uniqueId}`}
              // Required for the outlined border to open a notch for the floated label;
              // without it the border line strikes through the label text.
              label={label}
              value={muiValue}
              onChange={handleChange}
              fullWidth={true}
              sx={{ height: 56 }}
              onClose={() => setSearchText('')} // Clear search on close
              displayEmpty={!!placeholder || other.displayEmpty}
              {...other}
              // Add search input if search prop is true
              onOpen={() => {
                setSearchText('');
                // Focus search input when menu opens
                setTimeout(() => {
                  searchInputRef.current?.focus();
                }, 100);
              }}
              MenuProps={{
                // Keep menu open when selecting items
                autoFocus: false,
                disableAutoFocus: true,
                disableEnforceFocus: true,
                // Prevent menu from stealing focus
                disableAutoFocusItem: true,
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    maxHeight: 380,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
                    '& .MuiList-root': { padding: 0 },
                  },
                },
              }}
              renderValue={(selected) => {
                const isEmpty = !Array.isArray(selected) || selected.length === 0;
                if (isEmpty) {
                  return placeholder
                    ? <span style={{ color: '#9ca3af' }}>{placeholder}</span>
                    : '';
                }
                if (allowExclusionsFromAll && isAllSelected) {
                  return excludedValues.length === 0
                    ? 'All'
                    : `All (excluding ${excludedValues.length})`;
                }
                if (isAllSelected) {
                  return 'All';
                }
                if (selectAll && selected.length > 2) {
                  return selected.length === allRealValues.length ? `All (${selected.length})` : `${selected.length} selected`;
                }
                return selected.map(value => options.find(opt => opt.value === value)?.label).join(', ');
              }}
            >
              {search && (
                <MenuItem
                  disableRipple
                  sx={{
                    p: '8px 10px',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #f1f5f9',
                    zIndex: 1,
                    cursor: 'default',
                    '&:hover': { backgroundColor: '#fff' },
                    '&.Mui-focusVisible': { backgroundColor: '#fff' },
                    '&.Mui-selected': { backgroundColor: '#fff' },
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.preventDefault()}
                  onKeyDown={(e) => { e.stopPropagation(); }}
                >
                  <TextField
                    inputRef={searchInputRef}
                    fullWidth
                    variant="outlined"
                    placeholder="Search…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') e.preventDefault();
                    }}
                    onMouseDown={(e) => { e.stopPropagation(); }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        fontSize: '13px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        height: 32,
                      },
                      '& .MuiOutlinedInput-input': { padding: '6px 10px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                        borderWidth: '1px',
                      },
                    }}
                  />
                </MenuItem>
              )}
              {showSelectAll && (
                // Disabled so MUI's Select doesn't treat a click on the row as picking an option;
                // the buttons take their own clicks.
                <MenuItem
                  disabled
                  sx={{
                    position: 'sticky', top: search ? 49 : 0, zIndex: 1, backgroundColor: '#fff', opacity: '1 !important',
                    borderBottom: '1px solid #f1f5f9', py: 0.5, px: 1.25, minHeight: 36,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                  }}
                >
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {pickedValues.length} of {allRealValues.length} selected
                  </span>
                  <span style={{ display: 'flex', gap: 4, pointerEvents: 'auto' }}>
                    <Button size="small" disabled={allShownPicked || shownValues.length === 0}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => { e.stopPropagation(); pickShown(); }}
                      sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minWidth: 0, py: 0.25 }}>
                      {searchText ? `Select ${shownValues.length} shown` : 'Select all'}
                    </Button>
                    <Button size="small" color="inherit" disabled={pickedValues.length === 0}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => { e.stopPropagation(); clearShown(); }}
                      sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minWidth: 0, py: 0.25, color: '#64748b' }}>
                      {searchText ? 'Clear shown' : 'Clear'}
                    </Button>
                  </span>
                </MenuItem>
              )}
              {noOptionsAvailable ? (
                <MenuItem disabled value="" sx={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                  <em>{noOptionsText}</em>
                </MenuItem>
              ) : filteredOptions.length === 0 ? (
                <MenuItem disabled value="" sx={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic', justifyContent: 'center' }}>
                  No matches
                </MenuItem>
              ) : (
                filteredOptions.map((e, i) => (
                  <MenuItem
                    key={i}
                    value={e.value}
                    sx={{
                      py: 0.5,
                      px: 1,
                      fontSize: '13px',
                      minHeight: '34px',
                      transition: 'background-color 0.12s',
                      '&:hover': { backgroundColor: '#f1f5f9' },
                      '&.Mui-selected': { backgroundColor: '#eff6ff', color: '#1e293b' },
                      '&.Mui-selected:hover': { backgroundColor: '#dbeafe' },
                    }}
                    onClick={(evt) => { evt.stopPropagation(); }}
                  >
                    <Checkbox
                      size="small"
                      checked={
                        (allowExclusionsFromAll && isAllSelected)
                          ? muiValue.includes(e.value)
                          : (isAllSelected || selectedValues.includes(e.value))
                      }
                      // Presentational only — let the click bubble to the MenuItem, which is
                      // where MUI Select handles selection. stopPropagation here made clicks
                      // directly on the checkbox do nothing.
                      sx={{
                        pointerEvents: 'none',
                        p: 0.5,
                        mr: 0.75,
                        color: '#cbd5e1',
                        '&.Mui-checked': { color: '#1565c0' },
                        '& .MuiSvgIcon-root': { fontSize: 18 },
                      }}
                    />
                    {e.label}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>{error?.message}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
}
